import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { Task, TimelineEvent } from '../../../types';
import { CalendarEventType, CalendarDay, CalendarState } from './types';

export const useCalendar = (tasks: Task[], keyDates: TimelineEvent[]) => {
  const [currentDate, setCurrentDate] = useState(dayjs());

  const getEventsForDate = (date: dayjs.Dayjs): CalendarEventType[] => {
    const events: CalendarEventType[] = [];

    // Add tasks
    tasks.forEach(task => {
      if (typeof task.dueDate === 'string' && dayjs(task.dueDate).isSame(date, 'day')) {
        events.push({
          type: 'task',
          title: task.title,
          status: task.status,
          priority: task.priority,
        });
      }
    });

    // Add key dates and custom events
    keyDates.forEach(event => {
      if (dayjs(event.date).isSame(date, 'day')) {
        if (event.type === 'court-date') {
          events.push({
            type: 'court-date',
            title: event.description,
          });
        } else if (event.type === 'custom' && event.metadata?.eventType) {
          // Handle custom calendar events
          events.push({
            type: event.metadata.eventType as CalendarEventType['type'],
            title: event.description,
            priority: event.metadata.priority,
          });
        }
      }
    });

    return events;
  };

  const calendarState = useMemo((): CalendarState => {
    const daysInMonth = currentDate.daysInMonth();
    const firstDayOfMonth = currentDate.startOf('month').day();
    const weeks = Math.ceil((daysInMonth + firstDayOfMonth) / 7);
    
    // Generate calendar days
    const days: CalendarDay[][] = [];
    let currentDay = 1;

    for (let week = 0; week < weeks; week++) {
      const weekDays: (CalendarDay | null)[] = [];
      for (let day = 0; day < 7; day++) {
        if ((week === 0 && day < firstDayOfMonth) || currentDay > daysInMonth) {
          weekDays.push(null);
        } else {
          const date = currentDate.date(currentDay);
          weekDays.push({
            date,
            events: getEventsForDate(date),
          });
          currentDay++;
        }
      }
      days.push(weekDays as CalendarDay[]);
    }

    // Get upcoming events
    const today = dayjs();
    const upcomingEvents: { date: string; items: CalendarEventType[] }[] = [];

    // Add tasks
    tasks.forEach(task => {
      if (typeof task.dueDate === 'string') {
        const date = dayjs(task.dueDate);
        if (date.isAfter(today)) {
          const dateStr = date.format('YYYY-MM-DD');
          const existingDate = upcomingEvents.find(e => e.date === dateStr);
          if (existingDate) {
            existingDate.items.push({
              type: 'task',
              title: task.title,
              status: task.status,
              priority: task.priority,
            });
          } else {
            upcomingEvents.push({
              date: dateStr,
              items: [{
                type: 'task',
                title: task.title,
                status: task.status,
                priority: task.priority,
              }],
            });
          }
        }
      }
    });

    // Add key dates and custom events
    keyDates.forEach(event => {
      const date = dayjs(event.date);
      if (date.isAfter(today)) {
        const dateStr = date.format('YYYY-MM-DD');
        const existingDate = upcomingEvents.find(e => e.date === dateStr);
        
        let calendarEvent: CalendarEventType | null = null;
        
        if (event.type === 'court-date') {
          calendarEvent = {
            type: 'court-date',
            title: event.description,
          };
        } else if (event.type === 'custom' && event.metadata?.eventType) {
          calendarEvent = {
            type: event.metadata.eventType as CalendarEventType['type'],
            title: event.description,
            priority: event.metadata.priority,
          };
        }

        if (calendarEvent) {
          if (existingDate) {
            existingDate.items.push(calendarEvent);
          } else {
            upcomingEvents.push({
              date: dateStr,
              items: [calendarEvent],
            });
          }
        }
      }
    });

    return {
      currentDate,
      days,
      upcomingEvents: upcomingEvents.sort((a, b) => dayjs(a.date).diff(dayjs(b.date))),
    };
  }, [currentDate, tasks, keyDates]);

  const handlePrevMonth = () => {
    setCurrentDate(prev => prev.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => prev.add(1, 'month'));
  };

  return {
    ...calendarState,
    handlePrevMonth,
    handleNextMonth,
  };
};